/**
 * former - form glue (values / fill / dirty / submit pipeline), entry point.
 *
 * Usage:
 *   <form validate former former-endpoint="/users/save" former-refresh="#lister" former-guard>
 *   g.former(form | selector, options)   // attach manually (options in former.js)
 *   g.former.values(form)                // -> plain data object for g.fetch
 *   g.former.fill(form, record)          // record -> fields (components included)
 *   g.former.reset(form)                 // back to the authored defaults
 *   g.former.isDirty(form)               // -> boolean
 *
 * Depends on genus (g.fetch, g.busy, g.fieldError, toasts, g.translate) and
 * plays with validator (validator:submit starts the pipeline), dialog (the
 * unsaved-changes confirm) and lister (refresh after save) when present.
 * Full reference: docs/libs/former.md.
 */
import { Former, defaults } from './former.js';
import { readValues } from './values.js';

// Bump on every meaningful change to the library.
const VERSION = '0.2.2';

const resolveOne = (target) => (typeof target === 'string' ? document.querySelector(target) : target);

function former(target, options) {
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);
	const instances = els.map((form) => form.former || new Former(form, options));
	return instances.length === 1 ? instances[0] : instances;
}

former.version = () => VERSION;
former.defaults = defaults;
former.get = (target) => {
	const el = resolveOne(target);
	return (el && el.former) || null;
};
former.values = (target) => {
	const form = resolveOne(target);
	return form ? readValues(form) : {};
};
former.fill = (target, record) => {
	const form = resolveOne(target);
	return form ? former(form).fill(record) : Promise.resolve(null);
};
former.reset = (target) => {
	const form = resolveOne(target);
	return form ? former(form).reset() : Promise.resolve(null);
};
former.isDirty = (target) => {
	const form = resolveOne(target);
	return form ? former(form).isDirty() : false;
};

function autoInit() {
	former('form[former]');
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
else autoInit();

if (window.g) {
	window.g.former = former;
	window.g.registerLib('former');
} else {
	window.former = former;
}
